import { useEffect, useState } from 'react'
import { useWorkspace } from '../../context/useWorkspace'
import { getReliabilityLeaderboard, type ReliabilityLeaderboardRow } from '../../services/reliabilityService'
import { formatRoleLabel } from '../../utils/roleLabels'
import { useNls } from '../../nls/nlsProvider'
import { EmptyHubState } from '../Common'
import { Button } from '../Common/Button'

const scoreLabel = (score: number) => score >= 80 ? 'Excellent' : score >= 60 ? 'Reliable' : score >= 40 ? 'Watch' : 'Needs support'

export const LeaderboardView = () => {
  const { t } = useNls()
  const { activeWorkspaceId } = useWorkspace()
  const [rows, setRows] = useState<ReliabilityLeaderboardRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = () => {
    if (!activeWorkspaceId) return
    setLoading(true)
    setError('')
    void getReliabilityLeaderboard(activeWorkspaceId)
      .then((result) => setRows(result.rows))
      .catch((next) => setError(next instanceof Error ? next.message : 'Could not load reliability leaderboard.'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [activeWorkspaceId])

  return (
    <section className="leaderboardView h-full">
      <header className="leaderboardHeader">
        <div>
          <span className="settingsEyebrow">Reliability Engine</span>
          <h2>Team Reliability</h2>
        </div>
        <Button variant="tertiary" size="sm" disabled={loading} onClick={load}>{loading ? 'Refreshing...' : 'Refresh'}</Button>
      </header>
      {error && <div className="adminBanner isError">{error}</div>}
      <div className="leaderboardRows">
        {rows.length === 0 && !loading ? <EmptyHubState preset="leaderboard" title="No reliability scores have been calculated yet." compact /> : rows.map((row, index) => (
          <article key={row.uid} className="leaderboardRow">
            <span className="leaderboardRank">{index + 1}</span>
            <div>
              <strong>{row.displayName}</strong>
              <small>{formatRoleLabel(t, row.role)} · {scoreLabel(row.reliabilityScore)}</small>
            </div>
            <div className="leaderboardScore">
              <strong>{Math.round(row.reliabilityScore)}</strong>
              <span>/100</span>
            </div>
            <dl>
              <div><dt>Scans out</dt><dd>{row.reliabilityStats?.successfulScans ?? 0}</dd></div>
              <div><dt>Done</dt><dd>{(row.reliabilityStats?.completedChecklistItems ?? 0) + (row.reliabilityStats?.completedTasks ?? 0)}</dd></div>
              <div><dt>Blockers</dt><dd>{row.reliabilityStats?.blockerEvents ?? 0}</dd></div>
              <div><dt>Vouches</dt><dd>{row.reliabilityStats?.vouches ?? 0}</dd></div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  )
}
